import React, { useEffect, useState, useRef } from 'react'
import { EditorState, convertToRaw, ContentState, convertFromHTML, Modifier, SelectionState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { Editor } from 'react-draft-wysiwyg';

import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import styles from './TextEditor.module.scss';

const LINE_HEIGHT_OPTIONS = [
    { label: '1.0', value: '1' },
    { label: '1.15', value: '1.15' },
    { label: '1.5', value: '1.5' },
    { label: '2.0', value: '2' },
    { label: '2.5', value: '2.5' },
    { label: '3.0', value: '3' },
];

function LineHeightControl({ lineHeight, setLineHeight }) {
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(e) {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div ref={wrapperRef} className={styles.lineHeightControl}>
            <button
                type="button"
                className={styles.lineHeightButton}
                onClick={() => setOpen(!open)}
                title="Line Height"
            >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="11" y1="4" x2="21" y2="4" />
                    <line x1="11" y1="12" x2="21" y2="12" />
                    <line x1="11" y1="20" x2="21" y2="20" />
                    <polyline points="4 7 7 4 7 20 4 17" />
                    <polyline points="4 17 7 20" />
                </svg>
                <span style={{ marginLeft: 3, fontSize: 12 }}>{lineHeight}</span>
            </button>
            {open && (
                <div className={styles.lineHeightDropdown}>
                    {LINE_HEIGHT_OPTIONS.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            className={`${styles.lineHeightOption} ${lineHeight === opt.value ? styles.lineHeightOptionActive : ''}`}
                            onClick={() => { setLineHeight(opt.value); setOpen(false); }}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function TextEditor({ value, setData }) {

    const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
    const [lineHeight, setLineHeight] = useState('1.5');
    const editorRef = useRef(null);
    const editorWrapperRef = useRef(null);
    const debounceTimer = useRef(null);
    const prevHighlightRef = useRef(null);

    useEffect(() => {
        return () => clearTimeout(debounceTimer.current);
    }, []);

    useEffect(() => {
        if (prevHighlightRef.current) {
            prevHighlightRef.current.classList.remove('active-editor-block');
            prevHighlightRef.current = null;
        }
        const selection = editorState.getSelection();
        if (!selection.getHasFocus()) return;
        const blockKey = selection.getStartKey();
        const wrapper = editorWrapperRef.current;
        if (!wrapper) return;
        const blockEl = wrapper.querySelector(`[data-offset-key="${blockKey}-0-0"]`);
        if (blockEl) {
            blockEl.classList.add('active-editor-block');
            prevHighlightRef.current = blockEl;

            const scrollContainer = wrapper.querySelector(`.${styles.editor}`);
            if (scrollContainer) {
                const blockRect = blockEl.getBoundingClientRect();
                const containerRect = scrollContainer.getBoundingClientRect();
                if (blockRect.bottom > containerRect.bottom) {
                    scrollContainer.scrollTop += blockRect.bottom - containerRect.bottom + 40;
                } else if (blockRect.top < containerRect.top) {
                    scrollContainer.scrollTop -= containerRect.top - blockRect.top + 40;
                }
            }
        }
    }, [editorState]);

    useEffect(() => {
        if (!value) {
            setEditorState(EditorState.createEmpty());
            editorRef.current = null;
        } else if (value && !editorRef.current) {
            const div = document.createElement('div');
            div.innerHTML = value;
            const wrapper = div.firstElementChild;
            if (wrapper && wrapper.style.lineHeight) {
                setLineHeight(wrapper.style.lineHeight);
            }
            let html = value.replace(/\n/g, '<br/>');
            const blocksFromHTML = convertFromHTML(html);
            const contentState = ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap);
            setEditorState(EditorState.createWithContent(contentState));
            editorRef.current = true;
        }
    }, [value]);

    useEffect(() => {
        if (!editorRef.current) return;
        const rawContentState = convertToRaw(editorState.getCurrentContent());
        const markup = draftToHtml(rawContentState);
        setData(`<div style="line-height: ${lineHeight}">${markup}</div>`);
    }, [lineHeight]);

    const handlePastedText = (text, html, editorState) => {
        const contentState = editorState.getCurrentContent();
        const selectionState = editorState.getSelection();
        const newContentState = Modifier.replaceText(
            contentState,
            selectionState,
            text
        );
        const newEditorState = EditorState.push(
            editorState,
            newContentState,
            'insert-characters'
        );
        setEditorState(newEditorState);
        return 'handled';
    };

    const handleResizeStart = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const el = editorWrapperRef.current;
        if (!el) return;
        const startY = e.clientY;
        const startHeight = el.offsetHeight;
        const onMove = (ev) => {
            const newHeight = Math.max(300, Math.min(1000, startHeight + ev.clientY - startY));
            el.style.minHeight = '0px';
            el.style.height = `${newHeight}px`;
        };
        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    const handleEditorWrapperClick = (e) => {
        const wrapper = editorWrapperRef.current;
        if (!wrapper) return;
        const contentEl = wrapper.querySelector('.public-DraftEditor-content');
        if (!contentEl) return;
        const contentRect = contentEl.getBoundingClientRect();
        const lastChild = contentEl.lastElementChild;
        if (!lastChild) return;
        const lastChildRect = lastChild.getBoundingClientRect();
        if (e.clientY > lastChildRect.bottom && e.clientY <= contentRect.bottom + 200) {
            const content = editorState.getCurrentContent();
            const lastBlock = content.getLastBlock();
            const lastKey = lastBlock.getKey();
            const length = lastBlock.getLength();
            const newSelection = SelectionState.createEmpty(lastKey).merge({
                anchorOffset: length,
                focusOffset: length,
                hasFocus: true,
            });
            setEditorState(EditorState.forceSelection(editorState, newSelection));
        }
    };

    return (
        <div className={styles.resizeContainer}>
        <div ref={editorWrapperRef} className={styles.outerWrapper} onClick={handleEditorWrapperClick}>
            <Editor
                toolbarCustomButtons={[<LineHeightControl key="lineHeight" lineHeight={lineHeight} setLineHeight={setLineHeight} />]}
                toolbar={{
                    options: ['inline', 'list', 'textAlign', 'history', 'fontSize', 'fontFamily', 'link', 'blockType', 'image'],
                    inline: {
                        options: ['bold', 'italic', 'underline', 'strikethrough'],
                        bold: { className: 'undefined' },
                        italic: { className: 'undefined' },
                        underline: { className: 'undefined' },
                        strikethrough: { className: 'undefined' },
                        className: styles.inlineTools
                    },
                    blockType: {
                        inDropdown: true,
                        options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'Blockquote', 'Code'],
                        className: undefined,
                        component: undefined,
                        dropdownClassName: undefined,
                    },
                    list: {
                        className: styles.listTool,
                        options: ['unordered'],
                    },
                    textAlign: {
                        className: styles.listTool,
                        options: ['left', 'center', 'right'],
                    },
                    fontSize: {
                        icon: 'image',
                        options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96],
                        className: undefined,
                        component: undefined,
                        dropdownClassName: undefined,
                    },
                    fontFamily: {
                        options: ['Arial', 'Georgia', 'Impact', 'Tahoma', 'Times New Roman', 'Verdana'],
                        className: undefined,
                        component: undefined,
                        dropdownClassName: undefined,
                    },
                    link: {
                        inDropdown: false,
                        className: undefined,
                        component: undefined,
                        popupClassName: undefined,
                        dropdownClassName: undefined,
                        showOpenOptionOnHover: true,
                        defaultTargetOption: '_self',
                        options: ['link', 'unlink'],
                        linkCallback: undefined
                    },
                    image: {
                        className: undefined,
                        component: undefined,
                        popupClassName: undefined,
                        urlEnabled: true,
                        uploadEnabled: true,
                        alignmentEnabled: true,
                        uploadCallback: undefined,
                        previewImage: false,
                        inputAccept: 'image/gif,image/jpeg,image/jpg,image/png,image/svg',
                        alt: { present: false, mandatory: false },
                        defaultSize: {
                            height: 'auto',
                            width: 'auto',
                        },
                    },
                    history: {
                        className: styles.listTool,
                    },
                }}
                editorState={editorState}
                onEditorStateChange={(state) => {
                    setEditorState(state);
                }}
                handlePastedText={handlePastedText}
                onChange={(e) => {
                    clearTimeout(debounceTimer.current);
                    debounceTimer.current = setTimeout(() => {
                        const rawContentState = convertToRaw(editorState.getCurrentContent());
                        const markup = draftToHtml(rawContentState);
                        setData(`<div style="line-height: ${lineHeight}">${markup}</div>`);
                    }, 300);
                }}
                wrapperClassName={styles.editorWrapper}
                editorClassName={styles.editor}
                editorStyle={{ lineHeight }}
                toolbarClassName={styles.toolbar}
                spellCheck="true"

            />
        </div>
            <div className={styles.resizeFooter} onMouseDown={handleResizeStart}>
                <span className={styles.resizeHandle} />
            </div>
        </div>
    )
}

export default TextEditor
